import React from 'react';
import PropTypes from 'prop-types';
import { LuCalendarClock, LuCircleCheck, LuFileSearch, LuFingerprint, LuGlobe, LuImage, LuMonitorSmartphone, LuRotateCcw, LuUserCheck, LuUserRound } from 'react-icons/lu';
import DetailPane from '../../../components/data/DetailPane';
import useErrorReportText from '../errorReportText';
import { StatusBadge, StatusChooser, initialsOf, statusMeta } from './reportBits';

const SECTION_TITLE = 'tw-px-2 tw-pb-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500';

/**
 * The selected report's panel (replaces the old action bar): screenshot,
 * all details, the status (with who changed it), the full description and
 * the reporter. Docked beside the table on wide screens, a drawer otherwise.
 */
const ReportDetailPane = ({ docked, height, report, savingStatus, position, onPrev, onNext, onClose, onStatusChange, onViewScreenshot, onViewDetails }) => {
  const { label, formatDate, fieldText } = useErrorReportText();
  const paneLabel = label('panel.label');

  if (!report) return <DetailPane docked={docked} height={height} open={false} label={paneLabel} onClose={onClose} />;

  const completed = report.status === 'Tamamlandı';
  const actions = [
    report.screenshotUrl && { key: 'screenshot', label: label('panel.screenshot'), icon: LuImage, tint: 'violet', onClick: () => onViewScreenshot(report.screenshotUrl) },
    { key: 'details', label: label('panel.details'), icon: LuFileSearch, tint: 'blue', onClick: onViewDetails },
  ].filter(Boolean);

  const details = [
    { key: 'user', icon: LuUserRound, label: label('panel.user'), value: report.userName },
    { key: 'uid', icon: LuFingerprint, label: label('panel.userId'), value: report.uid, copyable: Boolean(report.uid) },
    { key: 'date', icon: LuCalendarClock, label: label('panel.date'), value: formatDate(report.timestamp) },
    { key: 'device', icon: LuMonitorSmartphone, label: label('panel.device'), value: fieldText(report, 'deviceInfo') },
    { key: 'browser', icon: LuGlobe, label: label('panel.browser'), value: fieldText(report, 'browserInfo') },
    report.statusUpdatedByName && { key: 'updater', icon: LuUserCheck, label: label('panel.updater'), value: report.statusUpdatedByName },
    report.statusUpdatedAt && { key: 'updatedAt', icon: LuCalendarClock, label: label('panel.updatedAt'), value: formatDate(report.statusUpdatedAt) },
  ].filter(Boolean);

  return (
    <DetailPane
      docked={docked}
      height={height}
      open
      label={paneLabel}
      heading={label('panel.heading')}
      position={position}
      contentKey={report.id}
      identity={{
        avatar: initialsOf(report.userName),
        gradient: statusMeta(report.status).gradient,
        title: report.errorSummary || label('table.noSummary'),
        subtitle: report.userName || undefined,
        badges: (
          <>
            <StatusBadge status={report.status} />
            {report.screenshotUrl && (
              <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-violet-500/10 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-violet-700 tw-ring-1 tw-ring-inset tw-ring-violet-500/25 dark:tw-text-violet-300">
                <LuImage aria-hidden="true" className="tw-h-3 tw-w-3" />
                {label('panel.withScreenshot')}
              </span>
            )}
          </>
        ),
      }}
      actions={actions}
      details={details}
      footer={completed
        ? { label: label('panel.reopen'), title: label('panel.reopenHint'), icon: LuRotateCcw, tone: 'danger', onClick: () => onStatusChange(report.id, 'Beklemede') }
        : { label: label('panel.markDone'), icon: LuCircleCheck, tone: 'success', onClick: () => onStatusChange(report.id, 'Tamamlandı') }}
      onPrev={onPrev}
      onNext={onNext}
      onClose={onClose}
    >
      <section aria-label={label('panel.statusTitle')} className="tw-px-1">
        <p className={SECTION_TITLE}>{label('panel.statusTitle')}</p>
        <StatusChooser status={report.status} busy={savingStatus} onChange={(value) => onStatusChange(report.id, value)} />
        <p className="tw-mt-2 tw-px-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
          {report.statusUpdatedByName
            ? label('panel.updatedBy', { name: report.statusUpdatedByName, date: formatDate(report.statusUpdatedAt) })
            : label('panel.notUpdated')}
        </p>
      </section>

      <section aria-label={label('panel.summary')} className="tw-px-1">
        <p className={SECTION_TITLE}>{label('panel.summary')}</p>
        <p className="tw-whitespace-pre-wrap tw-break-words tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/70 tw-px-3.5 tw-py-3 tw-text-sm tw-leading-relaxed tw-text-slate-700 dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:tw-text-slate-200">
          {report.errorSummary || label('table.noSummary')}
        </p>
      </section>

      {report.screenshotUrl && (
        <section aria-label={label('panel.screenshot')} className="tw-px-1">
          <p className={SECTION_TITLE}>{label('panel.screenshot')}</p>
          <button
            type="button"
            onClick={() => onViewScreenshot(report.screenshotUrl)}
            title={label('panel.screenshotHint')}
            className="tw-group tw-relative tw-block tw-w-full tw-overflow-hidden tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-100 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-border-white/10 dark:tw-bg-white/[0.04]"
          >
            <img src={report.screenshotUrl} alt={label('screenshot.alt')} loading="lazy" className="tw-h-40 tw-w-full tw-object-cover tw-object-top tw-transition-transform tw-duration-300 group-hover:tw-scale-[1.03]" />
            <span className="tw-absolute tw-bottom-2 tw-right-2 tw-rounded-full tw-bg-slate-900/70 tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-semibold tw-text-white">{label('panel.screenshotHint')}</span>
          </button>
        </section>
      )}
    </DetailPane>
  );
};

ReportDetailPane.propTypes = {
  docked: PropTypes.bool,
  height: PropTypes.number,
  report: PropTypes.object,
  savingStatus: PropTypes.bool,
  position: PropTypes.shape({ index: PropTypes.number, total: PropTypes.number }),
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  onViewScreenshot: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func.isRequired,
};

export default ReportDetailPane;
